// @flow

import * as React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import PropertyList, {PropertyListItem} from 'sail/PropertyList';
import {CaptionExtra, BodyMono} from 'sail/Text';
import Group from 'sail/Group';
import {getUserOS} from 'docs/util';
import {KEY_MAPPINGS as K} from '../helpers/cli/key_mappings';

import type {KeyMapping} from '../helpers/cli/key_mappings';

const messages = defineMessages({
  tipLabel: {
    id: 'docsApp.stripeShellShortcut.tipLabel',
    description:
      'Tooltip label of the dialog which displays keyboard shortcuts.',
    defaultMessage: 'Shortcuts',
  },
  title: {
    id: 'docsApp.stripeShellShortcut.title',
    description: 'Title of the dialog which displays keyboard shortcuts.',
    defaultMessage: 'Keyboard shortcuts',
  },
  close: {
    id: 'docsApp.stripeShellShortcut.close',
    description: 'Label for the action to close the dialog',
    defaultMessage: 'Close',
  },
  toggleShell: {
    id: 'docsApp.stripeShellShortcut.toggleShell',
    description:
      'Description of the key combination to toggle the Stripe Shell open',
    defaultMessage: 'Expand / Minimize the Stripe Shell',
  },
  nextPreviousHistory: {
    id: 'docsApp.stripeShellShortcut.nextPreviousHistory',
    description:
      'Description of the key combination to show previous or next command history.',
    defaultMessage: 'Next / Previous history',
  },
  highlightSuggestion: {
    id: 'docsApp.stripeShellShortcut.highlightSuggestion',
    description:
      'Description of the key combination to highlight a suggestion.',
    defaultMessage: 'Highlight next / previous suggestion',
  },
  copy: {
    id: 'docsApp.stripeShellShortcut.copy',
    description: 'Description of the key combination to copy.',
    defaultMessage: 'Copy',
  },
  paste: {
    id: 'docsApp.stripeShellShortcut.paste',
    description: 'Description of the key combination to paste.',
    defaultMessage: 'Paste',
  },
  clearPrompt: {
    id: 'docsApp.stripeShellShortcut.clearPrompt',
    description: 'Description of the key combination to clear the prompt.',
    defaultMessage: 'Clear the prompt',
  },
  deletePreviousWord: {
    id: 'docsApp.stripeShellShortcut.deletePreviousWord',
    description:
      'Description of the key combination to delete the previous word.',
    defaultMessage: 'Delete previous word',
  },
  clearTerminalOutput: {
    id: 'docsApp.stripeShellShortcut.clearTerminalOutput',
    description:
      'Description of the key combination to clear the terminal output.',
    defaultMessage: 'Clear terminal output',
  },
  cursorToBeginning: {
    id: 'docsApp.stripeShellShortcut.cursorToBeginning',
    description:
      'Description of the key combination to move the cursor to the beginning.',
    defaultMessage: 'Move cursor to the beginning of the prompt',
  },
  cursorToEnd: {
    id: 'docsApp.stripeShellShortcut.cursorToEnd',
    description:
      'Description of the key combination to move the cursor to the end.',
    defaultMessage: 'Move cursor to the end of the prompt',
  },
  focusPreviousPane: {
    id: 'docsApp.stripeShellShortcut.focusPreviousPane',
    description:
      'Description of the key combination to focus the previous pane.',
    defaultMessage: 'Focus previous pane',
  },
  focusNextPane: {
    id: 'docsApp.stripeShellShortcut.focusNextPane',
    description: 'Description of the key combination to focus the next pane.',
    defaultMessage: 'Focus next pane',
  },
  addPane: {
    id: 'docsApp.stripeShellShortcut.addPane',
    description: 'Description of the key combination to add a new pane.',
    defaultMessage: 'Add new pane',
  },
  removeCharactersToEndOfPrompt: {
    id: 'docsApp.stripeShellShortcut.removeCharactersToEndOfPrompt',
    description:
      'Description of the key combination to remove characters from the cursor to the end of the prompt.',
    defaultMessage:
      'Remove the characters from the cursor to the end of the prompt',
  },
  moveCursorByCharacter: {
    id: 'docsApp.stripeShellShortcut.moveCursorByCharacter',
    description:
      'Description of the key combination to move the cursor left and right by one character.',
    defaultMessage: 'Move the cursor left / right by one character',
  },
  removeCharacterUnderCursor: {
    id: 'docsApp.stripeShellShortcut.removeCharacterUnderCursor',
    description:
      'Description of the key combination to remove the character under the cursor.',
    defaultMessage: 'Remove the character under the cursor',
  },
});

const TOGGLE_SHELL_MAPPING: KeyMapping = {key: '`', modifiers: ['ctrlKey']};

const DOCUMENTED_KEYS = [
  [[TOGGLE_SHELL_MAPPING], messages.toggleShell],
  [[K.ARROW_DOWN, K.ARROW_UP], messages.nextPreviousHistory],
  [[K.TAB, K.SHIFT_TAB], messages.highlightSuggestion],
  [[K.CMD_C, K.CTRL_SHIFT_C], messages.copy],
  [[K.CMD_V, K.CTRL_V], messages.paste],
  [[K.CTRL_D], messages.removeCharacterUnderCursor],
  [[K.CTRL_W, K.ALT_BACKSPACE], messages.deletePreviousWord],
  [[K.CTRL_K], messages.removeCharactersToEndOfPrompt],
  [[K.CTRL_U], messages.clearPrompt],
  [[K.CMD_K, K.CTRL_L], messages.clearTerminalOutput],
  [[K.CTRL_B, K.CTRL_F], messages.moveCursorByCharacter],
  [[K.CTRL_A, K.CMD_ARROW_LEFT, K.CTRL_ARROW_LEFT], messages.cursorToBeginning],
  [[K.CTRL_E, K.CMD_ARROW_RIGHT, K.CTRL_ARROW_RIGHT], messages.cursorToEnd],
  [[K.CMD_LEFT_BRACKET, K.CTRL_LEFT_BRACKET], messages.focusPreviousPane],
  [[K.CMD_RIGHT_BRACKET, K.CTRL_RIGHT_BRACKET], messages.focusNextPane],
  [[K.CMD_D, K.ALT_SHIFT_D], messages.addPane],
];

const MAC_SHORTCUTS = DOCUMENTED_KEYS.map((shortcut) => [
  shortcut[0].filter((mapping) => !mapping?.isNonMacOnly),
  ...shortcut.slice(1),
]);

const NON_MAC_SHORTCUTS = DOCUMENTED_KEYS.map((shortcut) => [
  shortcut[0].filter((mapping) => !mapping?.isMacOnly),
  ...shortcut.slice(1),
]);

const getOSModifierLabels = () => {
  const userOS = getUserOS();

  if (userOS === 'mac') {
    return {
      metaKey: '⌘',
      altKey: '⌥',
    };
  } else if (userOS === 'windows') {
    return {
      metaKey: 'Win',
      altKey: '⎇',
    };
  } else {
    return {
      metaKey: 'Meta',
      altKey: 'Alt',
    };
  }
};

const mappingToString = (mapping) => {
  const modifiers = mapping?.modifiers ?? [];
  const modifierLabels = getOSModifierLabels();

  const cmd = modifiers.includes('metaKey') ? modifierLabels.metaKey : '';
  const ctrl = modifiers.includes('ctrlKey') ? '⌃' : '';
  const shift = modifiers.includes('shiftKey') ? '⇧' : '';
  const alt = modifiers.includes('altKey') ? modifierLabels.altKey : '';

  let key = mapping?.key ?? '';
  if (key.length === 1) {
    key = key.toUpperCase();
  }

  return [cmd, ctrl, alt, shift, key].filter(Boolean).join('+');
};

const KeyboardShortcuts = () => {
  const {formatMessage} = useIntl();
  const userOS = getUserOS();

  const shortcuts = userOS === 'mac' ? MAC_SHORTCUTS : NON_MAC_SHORTCUTS;

  return (
    <PropertyList layout="inline" size="medium" outerPadding={0}>
      {shortcuts.map(([keyCombos, description], idx) => (
        <PropertyListItem
          key={idx}
          label={
            <Group direction="horizontal" spacing={4} alignItems="baseline">
              {keyCombos.map((combo, keyComboIdx) => (
                <CaptionExtra
                  color="gray700"
                  style={{
                    padding: '0 6px',
                    background: 'var(--sail-color-gray-100)',
                    height: 20,
                    lineHeight: '20px',
                    borderRadius: 3,
                    display: 'inline-block',
                  }}
                  key={keyComboIdx}
                >
                  {mappingToString(combo)}
                </CaptionExtra>
              ))}
            </Group>
          }
          definition={
            <BodyMono color="gray200">
              {`-- ${formatMessage(description)}`}
            </BodyMono>
          }
        />
      ))}
    </PropertyList>
  );
};

export default KeyboardShortcuts;
